import { useMemo } from "react";
import { State } from "redux/store";
import { connect, useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";

import {
  setShowDuplicatesRange,
  setShowMislabelsRange,
} from "redux/Metadata/actions";
import {
  getDatasetIssueTypes,
  getDuplicateThresholdRange,
  getEntityTypeFilter,
  getIsDuplicatesThresholdEnabled,
  getIsMislabelesThresholdEnabled,
  getMislabelsThresholdRange,
} from "redux/SingleDataset/selectors";

import {
  DUPLICATE_THRESHOLD,
  ENTITY_TYPE_PARAMETER,
  ISSUE_CONFIDENCE,
  ISSUE_TYPE_PARAMETER,
  PAGE_PARAMETER,
} from "helpers/constants/miscellaneous";
import { trackFilterSelection } from "helpers/utility/amplitude";
import {
  DuplicatesThresholdRange,
  FilterOption,
  isValidEntityType,
} from "types";

import DropDown from "views/pages/DataPage/CardsView/DataPageFilters/DropDown";
import useQueryParam from "hooks/useQueryParam";
import FilterButton from "views/atomComponents/FilterButton";

interface IssueTypeFilterProps {
  isDuplicatesThresholdEnabled: boolean;
  duplicateThresholdRange: DuplicatesThresholdRange | null;
}

const IssueTypeFilter = ({
  duplicateThresholdRange,
  isDuplicatesThresholdEnabled,
}: IssueTypeFilterProps) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { datasetId, clusterId } = useParams();
  const entityParam = useQueryParam(ENTITY_TYPE_PARAMETER);
  const entityTypeFromQueryParam = isValidEntityType(entityParam)
    ? entityParam
    : null;
  const selectedIssueTypesQueryParamValue = JSON.parse(
    useQueryParam(ISSUE_TYPE_PARAMETER) || "[]"
  );
  const entityTypeFilter = useSelector((state: State) =>
    getEntityTypeFilter(state, entityTypeFromQueryParam)
  );
  const isMislabelesThresholdEnabled = useSelector((state: State) =>
    getIsMislabelesThresholdEnabled(state, entityTypeFromQueryParam)
  );
  const mislabelsThresholdRange = useSelector((state: State) =>
    getMislabelsThresholdRange(state, entityTypeFromQueryParam)
  );
  const datasetIssueTypes = useSelector((state: State) =>
    getDatasetIssueTypes(state, selectedIssueTypesQueryParamValue)
  );

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const selectedIssueTypes = datasetIssueTypes
    .filter((option: FilterOption) => option.selected)
    .map((option: FilterOption) => option.id);

  const issueTypesCountDisplayNumber =
    selectedIssueTypes?.length > 1 ? selectedIssueTypes?.length - 1 : 0;

  const firstIssueType = useMemo(() => {
    if (selectedIssueTypes?.length >= 1) {
      return datasetIssueTypes.find(
        (issue: FilterOption) => issue.id === selectedIssueTypes[0]
      )?.text;
    }
  }, [selectedIssueTypes, datasetIssueTypes]);

  const duplicateThreshold =
    searchParams.has(DUPLICATE_THRESHOLD) &&
    datasetIssueTypes.filter(
      (issue: FilterOption) => issue.text === "duplicates" && issue.selected
    ).length === 1
      ? parseFloat(searchParams.get(DUPLICATE_THRESHOLD) as string) || null
      : null;

  const handleFilterSelection = (value: number) => {
    let url = location.pathname;
    datasetIssueTypes.forEach((option: FilterOption) => {
      if (option.id === value) {
        //tracking checks
        if (option.selected) {
          trackFilterSelection(
            "Issue Types",
            "Dataset",
            entityTypeFilter,
            "Removed"
          );
        } else {
          trackFilterSelection(
            "Issue Types",
            "Dataset",
            entityTypeFilter,
            "Added"
          );
        }

        //remove mislabels confidence and duplicates threshold when respective issues get unselected
        const isMislabeled = ["mislabeles", "mislabels"].includes(
          option.text.toLowerCase()
        );
        const isDuplicate = option.text.toLowerCase() === "duplicates";

        if (isMislabeled && option.selected) {
          searchParams.delete(ISSUE_CONFIDENCE);
          dispatch(setShowMislabelsRange(false));
        }
        if (isDuplicate && option.selected) {
          searchParams.delete(DUPLICATE_THRESHOLD);
          dispatch(setShowDuplicatesRange(false));
          if (isDuplicatesThresholdEnabled && clusterId) {
            url = `/dataset/${datasetId}/data`;
          }
        }

        if (!option.selected) {
          const selectedEntity =
            entityTypeFilter.find((entity) => entity.selected)?.text ||
            "Images";

          //check and apply duplicateThreshold if allowed
          if (isDuplicate && isDuplicatesThresholdEnabled) {
            const range: any[] = duplicateThresholdRange
              ? selectedEntity === "Images"
                ? duplicateThresholdRange.imagesDuplicateThresholds
                : duplicateThresholdRange.objectsDuplicateThresholds
              : [];

            if (!!range.length) {
              const threshold = range[range.length - 1];

              searchParams.set(DUPLICATE_THRESHOLD, JSON.stringify(threshold));
              dispatch(setShowDuplicatesRange(true));
            }
          }
          // apply mislabeled issues confidence if allowed
          else if (isMislabeled && isMislabelesThresholdEnabled) {
            if (mislabelsThresholdRange) {
              searchParams.set(
                ISSUE_CONFIDENCE,
                JSON.stringify(mislabelsThresholdRange)
              );
              dispatch(setShowMislabelsRange(true));
            }
          }
        }
      }
    });
    const selectedIssueType = datasetIssueTypes.find(
      (option: FilterOption) => option.id === value
    );

    const newDatasetIssueTypes = [
      ...datasetIssueTypes.filter(
        (option: FilterOption) => option.id !== value
      ),
      { ...selectedIssueType, selected: !selectedIssueType?.selected },
    ];

    const result = newDatasetIssueTypes
      .filter((option) => option.selected === true)
      .map((option) => option.id);

    if (result.length > 0) {
      searchParams.set(ISSUE_TYPE_PARAMETER, JSON.stringify(result));
    } else {
      searchParams.delete(ISSUE_TYPE_PARAMETER);
    }
    searchParams.set(PAGE_PARAMETER, "1");
    navigate({ pathname: url, search: `?${searchParams.toString()}` });
  };

  return (
    <FilterButton
      label={"Issue Type"}
      selectedLabel={firstIssueType}
      itemCount={issueTypesCountDisplayNumber}
      confidenceLevel={duplicateThreshold ? duplicateThreshold : undefined}
      variant={selectedIssueTypes.length > 0 ? "selected" : "default"}
      tooltip={
        "The issue type filter is used to filter the issues in the dataset. It is used to filter the issues in the dataset."
      }
    >
      <DropDown
        selectedItemsIds={selectedIssueTypes}
        onItemSelection={handleFilterSelection}
        items={datasetIssueTypes}
      />
    </FilterButton>
  );
};

const mapStateToProps = (state: State) => {
  return {
    duplicateThresholdRange: getDuplicateThresholdRange(state),
    isDuplicatesThresholdEnabled: getIsDuplicatesThresholdEnabled(state),
  };
};

export default connect(mapStateToProps)(IssueTypeFilter);
